import { Injectable } from '@angular/core';
import { APIError, BOXSentryCaptureExceptionOptions } from '@box-types';
import { init, withScope, captureException, SeverityLevel, browserTracingIntegration } from '@sentry/angular';
import { environment } from '@box-env/environment';
import { isPrerenderBrowser, isBrowserHeadless } from '@box/utils';
import { Platform } from '@angular/cdk/platform';
class WrappedNonError extends Error {
  constructor(
    message: string,
    public original: unknown
  ) {
    super(message);
    this.name = 'NonErrorCaptured';
  }
}
@Injectable({ providedIn: 'root' })
export class SentryService {
  constructor(private platform: Platform) {}

  public init(): void {
    init({
      dsn: environment.application.SENTRY_DSN,
      environment: environment.code,
      release: `box-web@${String(environment.application.VERSION)}`,
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 0.02,
      ignoreErrors: ['Error: Non-Error exception captured with keys: error, headers, message, name, ok']
    });
  }

  public captureException(error: unknown, options?: BOXSentryCaptureExceptionOptions): void {
    if (this.isBotClient()) return;

    const safeError = error instanceof Error ? error : this.wrapNonError(error);

    withScope((scope) => {
      if (options?.domain) scope.setTag('box.domain', options.domain);
      if (options?.domainDetails) scope.setTag('box.domain.details', options.domainDetails);
      if (options?.severity) scope.setLevel(options.severity as SeverityLevel);
      captureException(safeError);
    });
  }

  private wrapNonError(error: unknown): Error {
    const message = this.extractMessage(error);

    // Optionally log for debugging (remove in prod if noisy)
    console.warn('[SentryService] Non-Error captured:', error);

    return new WrappedNonError(message, error);
  }

  private extractMessage(error: unknown): string {
    try {
      if (typeof error === 'string') return error;
      if (typeof error === 'number' || typeof error === 'boolean' || typeof error === 'symbol') {
        return `Captured primitive: ${String(error)}`;
      }
      if (typeof error === 'object' && error !== null) {
        // Try extracting 'message' if it has one
        if ('message' in error && typeof (error as any).message === 'string') {
          return (error as any).message;
        }
        return 'Captured non-Error object with keys: ' + Object.keys(error).join(', ');
      }
      if (error === null) return 'Captured null error';
      return 'Captured unknown error type';
    } catch (e) {
      return 'Failed to extract message from unknown error';
    }
  }

  private isBotClient(): boolean {
    return isPrerenderBrowser(window) || !this.platform.isBrowser || isBrowserHeadless();
  }
}
